// component SVG drawing layout tools
import { UserAgent } from '@mirinae/js-utils';
import { lessonsSizes } from '@mirinae/lessons/modules/defines/styles';

const { os } = UserAgent;
const isMobile = !os.isOthers;

export const getLayoutElement = (text, className) => {
    // return SVG text element of given class containing given text
    const t = document.querySelector(`#svg-layout-template > text.${className}`);
    t.textContent = text;
    return t;
};

export const getMetricsForStrings = (stringArray, className, fontSize) => {
    // computes 0,0-based metrics for each string in the given string array, assuming they will be rendered with the given CSS class
    //  all CSS classes used for font-metric calculations have font-size 10px, so desired font-size scale factor is 0.1.
    const fontSizeFactor = fontSize / 10;
    return stringArray.map(input => {
        if (input === ' ') {
            // handle single space separately, need to compute 0.25em for width of a space
            const text = getLayoutElement('\u3000', className); // u2003 is yields width of 1em in the font
            const bbox = text.getBBox();
            return {
                text: ' ',
                width: bbox.width * fontSizeFactor * 0.25, height: bbox.height * fontSizeFactor,
                offsets: [{ char: ' ', start: 0, end: bbox.width * fontSizeFactor * 0.25 }],
            };
        } else {
            const s = input/*.trim()*/.replace(/\s+/g, ' ');
            const text = getLayoutElement(s, className);
            const bbox = s.length === 0 ? { width: 0, height: 0 } : text.getBBox(); // Safari gives wrong bbox for empty strings!
            return {
                text: s,
                width: bbox.width * fontSizeFactor, height: bbox.height * fontSizeFactor,
                offsets: [...s].map((c, i) => {
                    const start = text.getStartPositionOfChar(i);
                    const end = text.getEndPositionOfChar(i);
                    return { char: c, start: start * fontSizeFactor, end: end * fontSizeFactor };
                }),
            };
        }
    });
};

// compute element layout geometry for the page & step based on device type, device size & figma-teamplate layout specs
export const layoutCurrentStep = (page, step, except, displayFilter, layoutFilter, paddingBottom = 0) => {
    const geom = (e) => (isMobile && e.mobile ? e.mobile : e).geom;
    const figmaPageBounds = isMobile ? (page.mobile ? page.mobile.lessonBounds : { x: 10, y: 99, width: 370, height: 720 }) : (page.lessonBounds || page.geom || { width: 1440, height: 900 });
    let stepElements = [];
    // grab all elements visible in this step & sort by y-coord
    page.children.slice(0, step.index + 1).reverse().find(s => {
        stepElements = stepElements.concat(s.children
            .filter(e => !e.containingFigmaFrameID && !e.managedByPage && e.componentName !== 'miniLessonTableCard')
            .filter(e => (layoutFilter ? layoutFilter(e) : (displayFilter ? displayFilter(e) : true)))
            .filter(e => !(except && except.includes(e.componentName))));
        return s.stepMode === 'replace'; // running backwards through steps, halt at first replacing step boundary
    });
    if (stepElements.length === 0)
        return [];
    //
    console.log(stepElements);
    stepElements.sort((e1, e2) => geom(e1).y - geom(e2).y);
    // construct col/row arrays, for now assumes a single column perhaps with rows
    const e0 = stepElements[0];
    let row = { elements: [e0], top: geom(e0).y - figmaPageBounds.y };
    const rows = [row];
    let rowBottom = geom(e0).y - figmaPageBounds.y + geom(e0).height;
    stepElements.slice(1).forEach(e => {
        if (geom(e).y - figmaPageBounds.y < rowBottom) {
            row.elements.push(e);
            rowBottom = Math.max(rowBottom, geom(e).y - figmaPageBounds.y + geom(e).height);
        } else {
            row.elements.sort((e1, e2) => geom(e1).x - geom(e2).x);
            row.bottom = rowBottom; row.height = row.bottom - row.top;
            row = { elements: [e], top: geom(e).y - figmaPageBounds.y };
            rowBottom = Math.max(rowBottom, geom(e).y - figmaPageBounds.y + geom(e).height);
            rows.push(row);
        }
    });
    row.elements.sort((e1, e2) => geom(e1).x - geom(e2).x);
    row.bottom = rowBottom; row.height = row.bottom - row.top;
    // have the sorted row/col arrays, add margins, widths, etc.
    let totalHeight;
    rowBottom = 0;
    rows.forEach(row => {
        // row margins
        row.left = geom(row.elements[0]).x - figmaPageBounds.x; row.right = row.left;
        row.margin = { left: row.left, top: row.top - rowBottom };
        rowBottom = row.bottom;
        // element margins
        let right = row.left;
        row.elements.forEach(e => {
            row.right = Math.max(row.right, geom(e).x - figmaPageBounds.x + geom(e).width);
            e.layout = { row, margin: { top: geom(e).y - figmaPageBounds.y - row.top, left: geom(e).x - figmaPageBounds.x - right } };
            right = geom(e).x - figmaPageBounds.x + geom(e).width;
        });
        row.width = row.right - row.left;
        row.margin.right = figmaPageBounds.width - row.right;
        totalHeight = row.bottom;
    });

    // run over rows again, adjust margins based on Figma layout styles & contraints to account for device dimensions
    // for now, we only adjust horizontal margins, allow the page to be as long as needed, prefering vertical to horizontal scrolling
    const lessonBounds = lessonsSizes.lessonBounds;
    rows.forEach(row => {
        if (lessonBounds.width !== figmaPageBounds.width) {
            // try to respect the element's Figma constraints
            //   - horizontal: CENTER => maintain width and relative pos, STRETCH => maintain margins, change width,
            //   - MIN: maintain width & left margin, MAX: maintain width & right margin
            // but for now, only if the row is a single-element row (use the element's constraints), since conflicts can arise if multiple elements causing overlap so treat as CENTER
            const e0 = row.elements[0];
            const constraints = row.elements.length === 1 ? (isMobile && e0.mobile ? e0.mobile : e0).constraints : { horizontal: 'CENTER', vertical: 'MIN' };
            let [marginLeft, marginRight] = [row.margin.left, row.margin.right];
            const marginDelta = Math.max(lessonBounds.minOuterlMargin * 2, lessonBounds.width - row.width);
            if (constraints.horizontal === 'CENTER') { // maintain width by relaxing edge margins
                marginLeft = marginDelta / 2;
                marginRight = marginLeft; //  hey, margins need to be proportinally-split based on maintaining relative center coord
            } else if (constraints.horizontal === 'MIN') {
                marginRight = marginDelta - row.margin.left;
            } else if (constraints.horizontal === 'MAX') {
                marginLeft = marginDelta - row.margin.right;
            } else if (constraints.horizontal === 'STRETCH') {
                // keep the margins
            }
            row.margin.left = Math.max(lessonBounds.minOuterlMargin, marginLeft);
            row.margin.right = Math.max(lessonBounds.minOuterlMargin, marginRight);
            row.width = lessonBounds.width - row.margin.left - row.margin.right;
        }
    });

    // for now, check page length on mobile, if > window height squeeze any top row top margin down to minTopMargin (100px per designers)
    // this should honor any constraints and also eat away at row-to-row margins to gain more vertical spacing
    if (isMobile) { // designers don't want this adaptivity; they are crazy!
        const padding = paddingBottom + 110 + (page.nextButton !== 'none' ? lessonsSizes.nextButton.height : 0);
        if (totalHeight + padding > lessonBounds.height) {
            // first trim half of needed margin from top
            const newTop = Math.max(lessonBounds.minTopMargin, rows[0].margin.top - (totalHeight + padding - lessonBounds.height) * 0.66);
            totalHeight -= rows[0].margin.top - newTop;
            rows[0].margin.top = newTop;
            const neededDelta = totalHeight + padding - lessonBounds.height;
            if (neededDelta > 0 && rows.length > 1) {
                // still overflowing, trim margin between rows proportionally
                const totalMargin = rows.slice(1).reduce((a, r) => a + r.margin.top, 0);
                rows.slice(1).forEach(r => r.margin.top = Math.max(lessonBounds.minRowMargin, r.margin.top - (r.margin.top / totalMargin) * neededDelta));
            }
        }
    }

    console.log('col-row layout', rows);
    return rows;
};
